import { useCallback, useState } from 'react';
import { getFabricAppExtensionInstalled, sendRequestOpenMultipleTabs } from '../services/extension';
import { isTruthy } from '@fabric-app/utils/guards';
import { useRunOnce } from '@fabric-app/hooks/useRunOnce';
import type { ResourceDetail } from '@fabric-app/types/resources';
import { getOriginUrlWithHash } from '@fabric-app/business/resources/utils/getOriginUrlWithHash';
import { confirm } from '@fabric-app/business/store/alerts';

type OpenInTabsOptions = {
  confirmed?: boolean;
  newWindow?: boolean;
};

const useOpenInTabs = () => {
  const [fabricExtensionInstalled, setFabricExtensionInstalled] = useState<boolean | null>(null);

  useRunOnce(() => {
    getFabricAppExtensionInstalled().then(setFabricExtensionInstalled);
  });

  const openInTabs = useCallback(
    async (resources: ResourceDetail[], options?: OpenInTabsOptions) => {
      if (!fabricExtensionInstalled) return;

      const urls = resources
        .map(
          (resource) => resource.originUrl && getOriginUrlWithHash(resource.originUrl, resource.id),
        )
        .filter(isTruthy);

      if (!urls.length) return;

      if (urls.length > 8) {
        const confirmed = await confirm({
          title: `That’s a lot of tabs!`,
          content: `You're about to open ${urls.length} links at once.`,
          confirmLabel: 'Yes, open all',
          cancelLabel: 'Cancel',
          confirmButtonProps: {
            variant: 'danger',
          },
        });

        if (!confirmed) {
          return;
        }
      }

      try {
        const response = await sendRequestOpenMultipleTabs(urls, options?.newWindow);

        if (!response) throw new Error('No response');
      } catch (e) {
        console.error(e);
      }
    },
    [fabricExtensionInstalled],
  );

  return {
    openInTabs,
    fabricExtensionInstalled,
  };
};

export default useOpenInTabs;
